import type { Block } from "./state"
import { assertNever, type AssemblyHint, type BlockSchema, type Face, type PressDirection, cutSize, cutLength, faceRoleKeys, getAssemblyHints } from "./blockschema"
import { inFraction } from "./plan_utils"

// One numbered step in an assembly section. `subs` are bullet items rendered
// under the main step in both markdown and PDF.
export interface AssemblyStep {
  main: string
  subs: string[]
}

// One labelled section of the assembly narrative — heading plus a list of
// numbered steps. `renderPlanMd` formats sections as markdown subheaders +
// numbered lists; `PageAssembly` formats them as PDF rows.
// `hint` is a string identifier (not rendered); patch-level hints use
// AssemblyHint values; schema-level sections use "chain_piecing" / "set_in_seam".
export interface AssemblySection {
  hint: string
  heading: string
  steps: AssemblyStep[]
}

// Every renderer that wants to write up a hint asks here. Templates read the
// schema and block to fill in cut sizes and counts so the narrative tracks
// `unit_inches` and `seam` automatically.
type Template = (schema: BlockSchema, block: Block) => AssemblySection

const TEMPLATES: Record<AssemblyHint, Template> = {
  flying_geese_no_waste: (schema, block) => {
    const large = schema.patches.find((p) => p.kind === "flying_goose_large")
    const small = schema.patches.find((p) => p.kind === "flying_goose_small")
    const geeseCount = small?.count ?? schema.faces.filter((f) => f.kind === "flying_goose").length
    const largeSq = large ? inFraction(cutSize(large, block)) : ""
    const smallSq = small ? inFraction(cutSize(small, block)) : ""
    return {
      hint: "flying_geese_no_waste",
      heading: "Flying Geese (no-waste 4-at-a-time)",
      steps: [
        {
          main: `Make ${geeseCount} flying geese per block (no-waste 4-at-a-time method):`,
          subs: [
            `Cut 1 large background square ${largeSq} and 4 small points squares ${smallSq} per block.`,
            "Draw both diagonals on the large background square.",
            "Place two small points squares on opposite corners of the large square, right sides together.",
            "Sew 1/4\" on each side of one diagonal; cut on that line; press open.",
            "Place a small square on the remaining corner of each unit, RST; sew and cut as before.",
            `Yields ${geeseCount} identical geese, no waste.`,
          ],
        },
      ],
    }
  },

  hst_two_at_a_time: (schema, block) => {
    const hstFaces = schema.faces.filter((f) => f.kind === "hst").length
    const hstPatch = schema.patches.find((p) => p.kind === "hst")
    const cutSq = hstPatch ? inFraction(cutSize(hstPatch, block)) : ""
    return {
      hint: "hst_two_at_a_time",
      heading: "Half-Square Triangles (two-at-a-time)",
      steps: [
        {
          main: `Make ${hstFaces} HSTs per block (two-at-a-time method):`,
          subs: [
            `Cut squares ${cutSq} from each fabric.`,
            "Pair one square of each fabric, right sides together. Mark the diagonal on the lighter square.",
            "Sew 1/4\" on each side of the marked diagonal.",
            "Cut on the diagonal line; press open — yields 2 HSTs per pair.",
            "Trim each HST square to the unfinished size; the seam runs corner to corner.",
          ],
        },
      ],
    }
  },

  strip_pieced: (schema, block) => {
    const stripPatches = schema.patches.filter((p) => p.kind === "strip")
    const stripFaces = schema.faces.filter((f) => f.kind === "strip").length
    // Every strip patch in a strip-pieced block shares a width and length;
    // pull from the first one for the instruction text.
    const first = stripPatches[0]
    const wid = first ? inFraction(cutSize(first, block)) : ""
    const len = first ? inFraction(cutLength(first, block)) : ""
    const fabricCount = stripPatches.length
    return {
      hint: "strip_pieced",
      heading: "Strip-pieced rails",
      steps: [
        {
          main: `Make ${stripFaces} strip${stripFaces === 1 ? "" : "s"} per block (strip-pieced method):`,
          subs: [
            `Cut ${wid}-wide strips selvage-to-selvage (WOF) from each of the ${fabricCount} rail fabrics — one strip of each fabric yields several blocks.`,
            "Sew the WOF strips lengthwise into a strip-set, alternating fabrics in the rail order; press seams toward the darker fabric so the seams nest when blocks meet.",
            `Cross-cut the strip-set every ${len} to yield one block-worth of joined rails.`,
            "Trim each unit square to the unfinished block size; the seam lines run parallel to the long edge.",
          ],
        },
      ],
    }
  },

  courthouse_steps: (schema, block) => {
    const stepPatches = schema.patches.filter((p) => p.assembly_hint === "courthouse_steps")
    const rings = Math.floor(stepPatches.length / 4)
    const firstStrip = stepPatches[0]
    const wid = firstStrip ? inFraction(cutSize(firstStrip, block)) : "?"
    const ringSteps: AssemblyStep[] = Array.from({ length: rings }, (_, i) => {
      const ring = stepPatches.slice(i * 4, i * 4 + 4) // [h_top, h_bot, v_lft, v_rgt]
      const hTop = ring[0], vLft = ring[2]
      const hLen = hTop ? inFraction(cutLength(hTop, block)) : "?"
      const vLen = vLft ? inFraction(cutLength(vLft, block)) : "?"
      return {
        main: `Ring ${i + 1}:`,
        subs: [
          `${wid} × ${hLen} ${hTop?.default_role ?? "light"} — sew to top, then bottom; press seams outward.`,
          `${wid} × ${vLen} ${vLft?.default_role ?? "dark"} — sew to left, then right; press seams outward.`,
        ],
      }
    })
    return {
      hint: "courthouse_steps",
      heading: "Courthouse Steps construction",
      steps: [
        {
          main: `All strips are ${wid} wide; lengths vary by ring (see cut table). Light fabric covers top and bottom of every ring; dark covers left and right.`,
          subs: [
            "Cut strips to the lengths in the cut table.",
            "Each ring: sew top first, then bottom — creates a tall column; press seams outward.",
            "Then sew left strip, then right — completes the ring square; press seams outward.",
          ],
        },
        ...ringSteps,
        { main: "Chain-piece: complete each step across all blocks before moving to the next.", subs: [] },
      ],
    }
  },

  log_cabin_spiral: (schema, block) => {
    const spiralPatches = schema.patches.filter((p) => p.assembly_hint === "log_cabin_spiral")
    const rings = Math.floor(spiralPatches.length / 4)
    const firstStrip = spiralPatches[0]
    const wid = firstStrip ? inFraction(cutSize(firstStrip, block)) : "?"
    // Spiral order within each ring: top (light) → right (dark) → bottom (dark) → left (light).
    const SIDES = ["top", "right", "bottom", "left"]
    const ringSteps: AssemblyStep[] = Array.from({ length: rings }, (_, i) => {
      const ring = spiralPatches.slice(i * 4, i * 4 + 4)
      const subs = ring.map((p, j) => {
        const len = inFraction(cutLength(p, block))
        return `${wid} × ${len} ${p.default_role} — sew to ${SIDES[j]}, press seam outward.`
      })
      return { main: `Ring ${i + 1}:`, subs }
    })
    return {
      hint: "log_cabin_spiral",
      heading: "Log Cabin spiral construction",
      steps: [
        {
          main: `All strips are ${wid} wide; lengths vary by position and ring (see cut table). Light fabric covers the top and left of every ring; dark covers the right and bottom.`,
          subs: [
            "Cut strips exactly to the lengths in the cut table — no trimming needed.",
            "Work each ring clockwise from the top: top → right → bottom → left.",
            "After each strip: fold open, press seam outward (away from center, toward the new strip).",
          ],
        },
        ...ringSteps,
        { main: "Chain-piece: complete each step across all blocks before moving to the next.", subs: [] },
      ],
    }
  },

  qst_four_at_a_time: (schema, block) => {
    const qstFaces = schema.faces.filter((f) => f.kind === "qst").length
    const qstPatch = schema.patches.find((p) => p.kind === "qst")
    const cutSq = qstPatch ? inFraction(cutSize(qstPatch, block)) : ""
    return {
      hint: "qst_four_at_a_time",
      heading: "Quarter-Square Triangles (four-at-a-time)",
      steps: [
        {
          main: `Make ${qstFaces} QST${qstFaces === 1 ? "" : "s"} per block (four-at-a-time method):`,
          subs: [
            `Cut one ${cutSq} square from each of the two QST fabrics.`,
            "Layer the two squares right sides together. Mark one diagonal on the lighter square.",
            "Sew 1/4\" on each side of that diagonal; cut on the line — yields 2 HSTs.",
            "Layer the two HSTs right sides together with opposite fabrics facing, seams nested. Mark the other diagonal across the seam.",
            "Sew 1/4\" on each side of the new diagonal; cut on the line — yields 4 QSTs with alternating fabrics meeting at the center.",
            "Trim each QST to the unfinished size; both seams cross at the center point.",
          ],
        },
      ],
    }
  },
}

// Primary patch label for a face: the label of the patch referenced by the
// first role key of the face. For flying geese (body+sky) this returns the
// body patch label since body is the first key.
function facePrimaryPatchLabel(face: Face, schema: BlockSchema): string {
  const key = faceRoleKeys(face)[0]
  const patchName = face.roles[key]
  return schema.patches.find((p) => p.name === patchName)?.label ?? patchName
}

type SeamGroup = { labelA: string; labelB: string; count: number; pressSet: Set<string> }

function accumulateEdge(
  groups: Map<string, SeamGroup>,
  la: string,
  lb: string,
  pressLabel: string,
) {
  const [ka, kb] = la <= lb ? [la, lb] : [lb, la]
  const key = `${ka}|||${kb}`
  const g = groups.get(key)
  if (g) {
    g.count++
    g.pressSet.add(pressLabel)
  } else {
    groups.set(key, { labelA: ka, labelB: kb, count: 1, pressSet: new Set([pressLabel]) })
  }
}

function groupsToSubs(groups: Map<string, SeamGroup>): string[] {
  return [...groups.values()]
    .sort((a, b) => b.count - a.count)
    .map(({ labelA, labelB, count, pressSet }) => {
      const pressStr = pressSet.size === 1 ? [...pressSet][0] : "see diagram for press direction"
      return `${labelA} + ${labelB}: ${count} seam${count !== 1 ? "s" : ""} per block — ${pressStr}`
    })
}

function pressLabel(press: PressDirection, la: string, lb: string): string {
  switch (press) {
    case "open": return "press open"
    case "to_a": return `press → ${la}`
    case "to_b": return `press → ${lb}`
    default:     return assertNever(press)
  }
}

// Chain-piecing groups derived from seam_graph: clusters identical seam pairs
// so the quilter can batch-sew all pairs of the same type across all blocks
// before pressing. Returns null for schemas with no seam graph.
// When any edge carries a stage number the output is split into one step per
// stage; otherwise a single flat step is produced (backward-compatible).
export function chainPiecingSection(schema: BlockSchema): AssemblySection | null {
  if (schema.seam_graph.length === 0) return null

  const hasStages = schema.seam_graph.some((e) => e.stage != null)

  if (hasStages) {
    const byStage = new Map<number, Map<string, SeamGroup>>()

    for (const edge of schema.seam_graph) {
      const fa = schema.faces[edge.a]
      const fb = schema.faces[edge.b]
      if (!fa || !fb) continue

      const la = facePrimaryPatchLabel(fa, schema)
      const lb = facePrimaryPatchLabel(fb, schema)
      const press = pressLabel(edge.press, la, lb)

      const stageNum = edge.stage ?? 0
      if (!byStage.has(stageNum)) byStage.set(stageNum, new Map())
      accumulateEdge(byStage.get(stageNum)!, la, lb, press)
    }

    const sortedStages = [...byStage.keys()].sort((a, b) => a - b)
    const steps = sortedStages.map((stageNum) => ({
      main: `Stage ${stageNum}:`,
      subs: groupsToSubs(byStage.get(stageNum)!),
    }))

    return { hint: "chain_piecing", heading: "Chain-piecing order", steps }
  }

  // Flat (unstaged) path — unchanged behavior for schemas without stage numbers.
  const groups = new Map<string, SeamGroup>()

  for (const edge of schema.seam_graph) {
    const fa = schema.faces[edge.a]
    const fb = schema.faces[edge.b]
    if (!fa || !fb) continue

    const la = facePrimaryPatchLabel(fa, schema)
    const lb = facePrimaryPatchLabel(fb, schema)
    accumulateEdge(groups, la, lb, pressLabel(edge.press, la, lb))
  }

  if (groups.size === 0) return null

  return {
    hint: "chain_piecing",
    heading: "Chain-piecing order",
    steps: [
      {
        main: "Batch identical seam pairs across all blocks — sew without lifting the presser foot between pairs, then snip apart and press:",
        subs: groupsToSubs(groups),
      },
    ],
  }
}

// Y-seam warning: fires when any seam_graph edge carries set_in=true.
// No registered schema uses set_in today; this section fires automatically
// when LeMoyne Star or any other set-in block is registered.
export function yseamSection(schema: BlockSchema): AssemblySection | null {
  if (!schema.seam_graph.some((e) => e.set_in)) return null
  return {
    hint: "set_in_seam",
    heading: "Y-seams (set-in stitching)",
    steps: [
      {
        main: "This block has Y-seams where three seam allowances meet at a single point. Set-in seams cannot be chain-pieced.",
        subs: [
          "Mark the seam-allowance intersection (¼\" from each edge) on the wrong side of each piece at every Y junction.",
          "Stop all stitching exactly at the marked intersection — never sew across the seam allowances.",
          "Sew each leg of the Y separately, starting at the intersection and sewing outward toward the raw edge.",
          "Secure the thread at each intersection; pivot the fabric rather than easing it.",
          "Press each completed leg before sewing the next.",
        ],
      },
    ],
  }
}

// Walk the schema's hints (in declaration order) and emit one section per
// hint. Appends chain-piecing and Y-seam sections when the seam_graph has
// edges. Returns an empty array for schemas with no hints and no seam graph.
export function assemblySections(schema: BlockSchema, block: Block): AssemblySection[] {
  const sections: AssemblySection[] = getAssemblyHints(schema).map((h) => TEMPLATES[h](schema, block))
  const chain = chainPiecingSection(schema)
  if (chain) sections.push(chain)
  const yseam = yseamSection(schema)
  if (yseam) sections.push(yseam)
  return sections
}
